import { PercentageOutlined } from "@ant-design/icons";
import Algorithm from "@components/global/new/Algorithm/Algorithm";
import EditableValueField from "@components/global/new/EditableValueField/EditableValueField";
import { ALGOS_E } from "@constants/Algos";
import {
  IMinerProfileEditorStore,
  MinerProfileAlgo,
} from "@mobx/store/minerProfiles/editor";
import { HashArr, HashFactorName, UNIT_NAME } from "@utils/hash.utils";
import { Select as AntSelect, InputNumber, Tooltip } from "antd";
import { observer } from "mobx-react-lite";
import { useIntl } from "react-intl";
import EfficiencyField from "../EfficiencyField/EfficiencyField";
import styles from "./MinerEfficiencyEditor.module.scss";

interface IMinerEfficiencyEditorProps {
  readonly editorStore: IMinerProfileEditorStore;
  readonly disabled?: boolean;
  readonly outlined?: boolean;
  readonly perfError?: boolean;
  readonly consumptionError?: boolean;
  readonly efficiencyError?: boolean;
  readonly algoError?: boolean;
}

const DECIMAL_INPUT_RE = /^\d*(\.\d*)?$/;

const PerformanceField = observer(function PerformanceField({
  editorStore,
  disabled,
  outlined,
  isError,
}: {
  readonly editorStore: IMinerProfileEditorStore;
  readonly disabled?: boolean;
  readonly outlined?: boolean;
  readonly isError?: boolean;
}) {
  const { formatMessage } = useIntl();
  const draft = editorStore.draft;

  if (!draft) return null;

  return (
    <EditableValueField
      id='perfomance'
      label={formatMessage({ id: "calc.perf" })}
      value={draft.performanceInput}
      pattern={DECIMAL_INPUT_RE}
      dataBage={draft.performanceFactor}
      disabled={disabled}
      readOnly={disabled}
      autoComplete="off"
      outlined={outlined}
      isError={isError}
      onChange={(value) => editorStore.setPerformanceInput(value)}
      addonAfter={
        <AntSelect
          size='small'
          className={styles.factorSelect}
          value={draft.performanceFactor}
          disabled={disabled}
          onChange={(value) =>
            editorStore.setPerformanceFactor(value as HashFactorName)
          }
          options={HashArr.map((factor) => ({
            label: `${factor}${UNIT_NAME[draft.hash]}`,
            value: factor,
          }))}
        />
      }
    />
  );
});

const PowerConsumptionField = observer(function PowerConsumptionField({
  editorStore,
  disabled,
  outlined,
  isError,
}: {
  readonly editorStore: IMinerProfileEditorStore;
  readonly disabled?: boolean;
  readonly outlined?: boolean;
  readonly isError?: boolean;
}) {
  const { formatMessage } = useIntl();
  const draft = editorStore.draft;

  if (!draft) return null;

  const err = Number(draft.err) || 0;
  const consumption = Number(draft.consumption) || 0;
  const errCons = Number(((consumption * err) / 100).toFixed(2));
  const consumptionWithErr = editorStore.effectiveConsumption;

  return (
    <div className={styles.consumptionWrapper}>
      <Tooltip
        trigger='click'
        open={disabled ? false : undefined}
        title={
          <div className={styles.percentEditor}>
            <InputNumber
              type='number'
              placeholder={formatMessage({ id: "calc.err" })}
              size='small'
              className={styles.percentField}
              value={err > 0 ? err : null}
              onChange={(value) => editorStore.setErr(Number(value) || 0)}
              controls={false}
              addonAfter='%'
            />
          </div>
        }
        color='white'
      >
        <div
          className={styles.percentContainer}
          title={formatMessage({ id: "calc.err" })}
        >
          {err > 0 && <span>{err}</span>}
          <PercentageOutlined style={{ fontSize: 12, color: "red" }} />
        </div>
      </Tooltip>
      <EditableValueField
        id='consumpation'
        label={formatMessage({ id: "calc.cons" })}
        value={draft.consumptionInput}
        pattern={DECIMAL_INPUT_RE}
        disabled={disabled}
        readOnly={disabled}
        autoComplete="off"
        outlined={outlined}
        isError={isError}
        onChange={(value) => editorStore.setConsumptionInput(value)}
        addonAfter={<span>{formatMessage({ id: "calc.cons.bage" })}</span>}
      />
      {errCons > 0 && (
        <p className={styles.calculated}>
          + {errCons} {formatMessage({ id: "calc.cons.bage" })}
        </p>
      )}
      {consumptionWithErr > 0 && err > 0 && (
        <p className={styles.consumptionWithErr}>
          {formatMessage({ id: "calc.perf.err" })} {consumptionWithErr}{" "}
          {formatMessage({ id: "calc.cons.bage" })}
        </p>
      )}
      <input hidden id='err' value={draft.err} readOnly />
    </div>
  );
});

const AlgoField = observer(function AlgoField({
  editorStore,
  disabled,
  outlined,
  isError,
}: {
  readonly editorStore: IMinerProfileEditorStore;
  readonly disabled?: boolean;
  readonly outlined?: boolean;
  readonly isError?: boolean;
}) {
  const { formatMessage } = useIntl();
  const draft = editorStore.draft;

  if (!draft) return null;

  //dbg.log("MINER_EFFICIENCY_EDITOR algo render:", { hash: draft.hash });

  return (
    <label>
      <span>{formatMessage({ id: "filters.algo" })}</span>
      <AntSelect
        size='small'
        value={draft.hash}
        disabled={disabled}
        status={isError ? "error" : ""}
        style={{ width: "100%" }}
        onChange={(value) => {
          //dbg.log("MINER_EFFICIENCY_EDITOR algo onChange:", { value });
          editorStore.setHash(value as MinerProfileAlgo);
        }}
        options={Object.values(ALGOS_E)
          .filter((algo) => algo !== ALGOS_E.ALL)
          .map((algo) => ({
            label: (
              <Algorithm
                iconSize={16}
                algo={algo}
                hashName={algo.toUpperCase()}
                string
              />
            ),
            value: algo,
          }))}
      />
      <input hidden readOnly id='hash' value={draft.hash} />
    </label>
  );
});

function MinerEfficiencyEditor({
  editorStore,
  disabled,
  outlined,
  perfError,
  consumptionError,
  efficiencyError,
  algoError,
}: IMinerEfficiencyEditorProps) {
  const draft = editorStore.draft;

  if (!draft) return null;

  return (
    <div className={styles.wrapper}>
      <PerformanceField
        editorStore={editorStore}
        disabled={disabled}
        outlined={outlined}
        isError={perfError}
      />
      <PowerConsumptionField
        editorStore={editorStore}
        disabled={disabled}
        outlined={outlined}
        isError={consumptionError}
      />
      <EfficiencyField
        editorStore={editorStore}
        isError={efficiencyError}
        disabled={disabled}
      />
      <AlgoField
        editorStore={editorStore}
        disabled={disabled}
        outlined={outlined}
        isError={algoError}
      />
    </div>
  );
}

export default observer(MinerEfficiencyEditor);
